import React, { useState, createContext, useContext, ReactNode } from 'react';
type Language = 'en' | 'de' | 'pt' | 'fr' | 'es' | 'it' | 'nl';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
interface LanguageProviderProps {
  children: ReactNode;
}
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children
}) => {
  const [language, setLanguage] = useState<Language>('en');
  const translations: Record<Language, Record<string, string>> = {
    en: {
      'nav.home': 'Home',
      'nav.whyMushrooms': 'Why This Matters',
      'nav.spices': 'Functional Spices',
      'nav.aiCoach': 'AI Coach',
      'nav.howItWorks': 'How It Works',
      'nav.community': 'Community',
      'nav.joinWaitlist': 'Join Waitlist',
      'nav.getCoach': 'Get Coach',
      'hero.badge': 'Coming Soon',
      'hero.tagline': 'Season with Reason',
      'hero.hook': 'Supplements need discipline. Spices just need dinner.',
      'hero.subtitle': "The world's first adaptogenic spice range. Three clean label blends that support your mental wellness. No pills, no powders, no extra steps.",
      'hero.impact': 'Every 100 packs will fund one hour of therapy.',
      'hero.cta.primary': 'Join the Waitlist',
      'hero.image.alt': 'NeuroSpice Energy blend falling onto golden fries, with the Energy tin behind',
      'problem.title': 'Why This Matters',
      'problem.subtitle': "Mental health support should be accessible to everyone. We're building a model where every purchase creates impact.",
      'problem.stat1.value': '1 in 5',
      'problem.stat1.label': 'Adults experience mental illness annually',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Report nutrition impacts their mental health',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Average monthly therapy cost in Europe',
      'problem.impact.title': "Mental health support shouldn't be a luxury.",
      'problem.impact.text': "Every 100 NeuroSpice packs sold will fund one hour of therapy for someone who can't afford it. Flavour that gives back.",
      'problem.cta': 'Join the Waitlist',
      'useCases.badge': 'In the Wild',
      'useCases.title': 'Built Into Your Day.',
      'useCases.subtitle': 'Not a routine. Not a ritual. Just better versions of things you already do.',
      'useCases.case1.tag': 'Breakfast',
      'useCases.case1.product': 'Energy Blend · Cordyceps x Paprika',
      'useCases.case1.title': 'Breakfast, Fired Up.',
      'useCases.case1.description': 'A shake over shakshouka turns breakfast into an energy boost. Cordyceps and paprika. Straight into the pan.',
      'useCases.case2.tag': 'Cocktail Hour',
      'useCases.case2.product': 'Rest Blend · Reishi x Salt',
      'useCases.case2.title': 'Rim the Glass. Unwind.',
      'useCases.case2.description': 'Bartenders are using the Rest Blend to rim cocktails and wind down the night. Reishi and salt, by the sip.',
      'useCases.b2b.tag': 'For Bars & Restaurants',
      'useCases.b2b.title': 'Running a Venue?',
      'useCases.b2b.description': "Bulk pricing, custom blends, and on-menu storytelling. Let's talk.",
      'useCases.b2b.cta': 'Get in Touch',
      'subscription.badge': 'The Range',
      'subscription.title': 'Pick Your Pack.',
      'subscription.subtitle': 'Single, Duo, or the complete Poly-Pack. The more you stock, the less you spend per serving.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'One blend, your choice.',
      'subscription.bundle.single.contents': '1 spice',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Any two blends. Mix your day.',
      'subscription.bundle.duo.contents': '2 spices',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'All three: Focus, Energy & Rest.',
      'subscription.bundle.poly.contents': '3 spices',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Best Value',
      'subscription.perServing.label': 'per serving',
      'subscription.subscribe': 'Subscribe & Save',
      'subscription.subscribe.description': 'Monthly delivery, cancel anytime.',
      'subscription.save': 'Save 15%',
      'subscription.cta': 'Join the Waitlist',
      'subscription.note': "No payment now. We'll email you at launch.",
      'subscription.impact': 'Every box funds therapy access.',
      'comparison.badge': 'The Smarter Way',
      'comparison.title': 'Why Spices Beat Supplements',
      'comparison.subtitle': 'No new habits. No extra steps. Just better meals.',
      'comparison.col.capsules': 'Capsules',
      'comparison.col.gums': 'Gums',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Requires new habit',
      'comparison.row2': 'Integrates into meals',
      'comparison.row3': 'Daily consistency',
      'comparison.row4': 'Enjoyable to take',
      'comparison.row5': 'Funds therapy access',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Hard',
      'comparison.val.medium': 'Medium',
      'comparison.val.effortless': 'Effortless',
      'comparison.val.okay': 'Okay',
      'functional.badge': "World's First Adaptogenic Spices. Coming Soon",
      'functional.title': 'Functional Spice Range',
      'functional.subtitle': 'Three clean label blends. Two ingredients each: one adaptogen, one spice.',
      'functional.pending': 'Spiced supplement pre-sale at Bikini Berlin, 9 & 10 October, as part of',
      'functional.presale.link': 'House of Food at Berlin Food Week',
      'functional.focus.name': 'FOCUS',
      'functional.focus.subtitle': 'Morning Ritual',
      'functional.focus.spice': "Lion's Mane x Cinnamon",
      'functional.focus.benefits': 'Clean label. 2 ingredients.',
      'functional.focus.description': 'Mental clarity. Sustained attention.',
      'functional.energy.name': 'ENERGY',
      'functional.energy.subtitle': 'Day Power',
      'functional.energy.spice': 'Cordyceps x Paprika',
      'functional.energy.benefits': 'Clean label. 2 ingredients.',
      'functional.energy.description': 'Raw power. Natural momentum.',
      'functional.calm.name': 'REST',
      'functional.calm.subtitle': 'Unwind',
      'functional.calm.spice': 'Reishi x Salt',
      'functional.calm.benefits': 'Clean label. 2 ingredients.',
      'functional.calm.description': 'Gentle anxiety relief. Smooth transitions.',
      'functional.rest.name': 'REST',
      'functional.rest.subtitle': 'Sleep Well',
      'functional.rest.spice': 'Smoked Salt',
      'functional.rest.benefits': 'Reishi, Valerian, Magnesium',
      'functional.rest.description': 'Deep rest. Quality sleep.',
      'functional.waitlist.title': 'Join the Waitlist',
      'functional.waitlist.description': 'Be the first to get our adaptogenic spice range. Join the waitlist for early access when we launch in UK and EU markets.',
      'functional.waitlist.note': 'Limited early access. No payment required',
      'functional.waitlist.placeholder': 'your@email.com',
      'functional.waitlist.cta': 'Join Waitlist',
      'functional.waitlist.success': "✓ You're on the list",
      'howItWorks.title': 'How It Works',
      'howItWorks.subtitle': 'Simple. Meaningful. Impactful.',
      'howItWorks.step1.title': 'Join the Waitlist',
      'howItWorks.step1.description': 'Join the waitlist for early access to our adaptogenic spice range. No payment required.',
      'howItWorks.step2.title': 'Season Your Meals',
      'howItWorks.step2.description': 'Add functional flavour to your daily cooking. Support your mental wellness with every meal.',
      'howItWorks.step3.title': 'Fund Therapy Access',
      'howItWorks.step3.description': 'Every 100 packs sold will fund one hour of therapy for someone who needs it. Flavour that gives back.',
      'howItWorks.closing.title': 'Better mental health starts in your kitchen.',
      'howItWorks.closing.subtitle': 'Every meal is an opportunity to care for yourself and others.',
      'comingSoon.coach.badge': 'Coming Soon',
      'comingSoon.coach.title': 'Your AI Coach',
      'comingSoon.coach.description': 'Gentle daily support between therapy sessions. Track your mood, get personalized nutrition nudges, and prepare insights for your therapist.',
      'comingSoon.coach.form.title': 'Join the Beta',
      'comingSoon.coach.form.placeholder': 'your@email.com',
      'comingSoon.coach.form.cta': 'Join Waitlist',
      'comingSoon.coach.form.success': "✓ You're on the list",
      'comingSoon.coach.feature1.title': 'Daily Check-ins',
      'comingSoon.coach.feature1.description': 'Quick mood tracking that learns your patterns and rhythms.',
      'comingSoon.coach.feature2.title': 'Micro-Actions',
      'comingSoon.coach.feature2.description': "Small, doable nutrition suggestions tailored to how you're feeling.",
      'comingSoon.coach.feature3.title': 'Therapy Prep',
      'comingSoon.coach.feature3.description': 'Structured insights to bring to your next session.',
      'comingSoon.fund.badge': 'Coming Soon',
      'comingSoon.fund.title': 'The Fund',
      'comingSoon.fund.description': 'Expanding access to mental health care. Apply for funded therapy sessions or contribute to help others access the support they need.',
      'comingSoon.fund.form.title': 'Join the Waitlist',
      'comingSoon.fund.form.placeholder': 'your@email.com',
      'comingSoon.fund.form.cta': 'Get Notified',
      'comingSoon.fund.form.success': "✓ You're on the list",
      'comingSoon.fund.feature1.title': 'Apply for Support',
      'comingSoon.fund.feature1.description': 'Access funded therapy sessions when you need them most.',
      'comingSoon.fund.feature2.title': 'Community-Powered',
      'comingSoon.fund.feature2.description': 'Every purchase contributes. Every contribution matters.',
      'comingSoon.fund.feature3.title': 'Transparent Impact',
      'comingSoon.fund.feature3.description': 'See exactly how your support helps real people access care.',
      'roadmap.badge': "What's Next",
      'roadmap.title': 'Beyond the Spices',
      'roadmap.subtitle': 'Once we launch to our first waitlist members, we will open two more programmes to deepen the impact.',
      'roadmap.coach.title': 'AI Wellness Coach',
      'roadmap.coach.description': 'Gentle daily support between therapy sessions. Mood tracking, nutrition nudges, and therapy prep.',
      'roadmap.coach.status': 'Opens After Launch',
      'roadmap.fund.title': 'The Therapy Fund',
      'roadmap.fund.description': 'Every 100 packs funds one hour of therapy. Apply for funded sessions or contribute to expand access.',
      'roadmap.fund.status': 'Opens After Launch',
      'roadmap.note': 'Join the waitlist today to unlock both.',
      'social.title': 'Follow the Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Season with Reason',
      'footer.subtitle': 'Get Flavour. Give Therapy.',
      'footer.cta.waitlist': 'Join Waitlist',
      'footer.cta.coach': 'Join Beta',
      'footer.cta.fund': 'Join Waitlist',
      'floating.text': 'Join the Neurolution'
    },
    de: {
      'nav.home': 'Startseite',
      'nav.whyMushrooms': 'Warum das wichtig ist',
      'nav.spices': 'Funktionale Gewürze',
      'nav.aiCoach': 'KI-Coach',
      'nav.howItWorks': 'So funktioniert es',
      'nav.community': 'Gemeinschaft',
      'nav.joinWaitlist': 'Warteliste',
      'nav.getCoach': 'Coach holen',
      'hero.badge': 'Demnächst',
      'hero.tagline': 'Mit Vernunft würzen',
      'hero.hook': 'Nahrungsergänzung braucht Disziplin. Gewürze brauchen nur Abendessen.',
      'hero.subtitle': 'Die weltweit erste adaptogene Gewürzreihe. Drei Clean-Label-Mischungen, die Ihr mentales Wohlbefinden unterstützen. Keine Pillen, keine Pulver, keine zusätzlichen Schritte.',
      'hero.impact': 'Jede 100 Packungen werden eine Stunde Therapie finanzieren.',
      'hero.cta.primary': 'Auf die Warteliste',
      'hero.image.alt': 'Hand streut NeuroSpice in einen Wok mit frischem Gemüse in einer modernen Küche',
      'problem.title': 'Warum das wichtig ist',
      'problem.subtitle': 'Unterstützung der psychischen Gesundheit sollte für alle zugänglich sein. Wir bauen ein Modell, bei dem jeder Kauf Wirkung erzeugt.',
      'problem.stat1.value': '1 von 5',
      'problem.stat1.label': 'Erwachsene erleben jährlich psychische Erkrankungen',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Berichten, dass Ernährung ihre psychische Gesundheit beeinflusst',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Durchschnittliche monatliche Therapiekosten in Europa',
      'problem.impact.title': 'Unterstützung der psychischen Gesundheit sollte kein Luxus sein.',
      'problem.impact.text': 'Alle 100 verkauften NeuroSpice-Packungen werden eine Stunde Therapie für jemanden finanzieren, der sie sich nicht leisten kann. Geschmack, der zurückgibt.',
      'problem.cta': 'Auf die Warteliste',
      'useCases.badge': 'Im Alltag',
      'useCases.title': 'In Ihren Tag eingebaut.',
      'useCases.subtitle': 'Keine Routine. Kein Ritual. Nur bessere Versionen von Dingen, die Sie bereits tun.',
      'useCases.case1.tag': 'Frühstück',
      'useCases.case1.product': 'Energie-Mischung · Cordyceps x Paprika',
      'useCases.case1.title': 'Frühstück, angefeuert.',
      'useCases.case1.description': 'Eine Prise über Shakshuka macht das Frühstück zum Energieschub. Cordyceps und Paprika. Direkt in die Pfanne.',
      'useCases.case2.tag': 'Cocktail-Stunde',
      'useCases.case2.product': 'Erholungs-Mischung · Reishi x Salz',
      'useCases.case2.title': 'Den Glasrand würzen. Entspannen.',
      'useCases.case2.description': 'Bartender nutzen die Erholungs-Mischung für Cocktailränder und ruhige Abende. Reishi und Salz, schluckweise.',
      'useCases.b2b.tag': 'Für Bars & Restaurants',
      'useCases.b2b.title': 'Betreiben Sie eine Location?',
      'useCases.b2b.description': 'Großhandelspreise, individuelle Mischungen und Menü-Storytelling. Sprechen wir.',
      'useCases.b2b.cta': 'Kontakt aufnehmen',
      'subscription.badge': 'Das Sortiment',
      'subscription.title': 'Wählen Sie Ihr Pack.',
      'subscription.subtitle': 'Single, Duo oder das komplette Poly-Pack. Je mehr Sie bevorraten, desto weniger zahlen Sie pro Portion.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Eine Mischung Ihrer Wahl.',
      'subscription.bundle.single.contents': '1 Gewürz',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Zwei Mischungen. Mischen Sie Ihren Tag.',
      'subscription.bundle.duo.contents': '2 Gewürze',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Alle drei: Fokus, Energie & Erholung.',
      'subscription.bundle.poly.contents': '3 Gewürze',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Bester Wert',
      'subscription.perServing.label': 'pro Portion',
      'subscription.subscribe': 'Abonnieren & Sparen',
      'subscription.subscribe.description': 'Monatliche Lieferung, jederzeit kündbar.',
      'subscription.save': '15% sparen',
      'subscription.cta': 'Auf die Warteliste',
      'subscription.note': 'Keine Zahlung jetzt. Wir benachrichtigen Sie zum Start.',
      'subscription.impact': 'Jede Box finanziert Therapiezugang.',
      'comparison.badge': 'Der clevere Weg',
      'comparison.title': 'Warum Gewürze besser sind als Nahrungsergänzung',
      'comparison.subtitle': 'Keine neuen Gewohnheiten. Keine zusätzlichen Schritte. Einfach bessere Mahlzeiten.',
      'comparison.col.capsules': 'Kapseln',
      'comparison.col.gums': 'Kaugummis',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Erfordert neue Gewohnheit',
      'comparison.row2': 'Integriert sich in Mahlzeiten',
      'comparison.row3': 'Tägliche Konsistenz',
      'comparison.row4': 'Angenehm einzunehmen',
      'comparison.row5': 'Finanziert Therapiezugang',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Schwer',
      'comparison.val.medium': 'Mittel',
      'comparison.val.effortless': 'Mühelos',
      'comparison.val.okay': 'Okay',
      'functional.badge': 'Weltweit erste adaptogene Gewürze. Demnächst',
      'functional.title': 'Funktionale Gewürzreihe',
      'functional.subtitle': 'Drei Clean-Label-Mischungen. Je zwei Zutaten: ein Adaptogen, ein Gewürz.',
      'functional.pending': 'Vorverkauf unserer Gewürz-Supplements im Bikini Berlin am 9. & 10. Oktober im Rahmen von',
      'functional.presale.link': 'House of Food der Berlin Food Week',
      'functional.focus.name': 'FOKUS',
      'functional.focus.subtitle': 'Morgenritual',
      'functional.focus.spice': 'Löwenmähne x Zimt',
      'functional.focus.benefits': 'Clean Label. 2 Zutaten.',
      'functional.focus.description': 'Geistige Klarheit. Anhaltende Aufmerksamkeit.',
      'functional.energy.name': 'ENERGIE',
      'functional.energy.subtitle': 'Tageskraft',
      'functional.energy.spice': 'Cordyceps x Paprika',
      'functional.energy.benefits': 'Clean Label. 2 Zutaten.',
      'functional.energy.description': 'Rohe Kraft. Natürlicher Schwung.',
      'functional.calm.name': 'ERHOLUNG',
      'functional.calm.subtitle': 'Entspannen',
      'functional.calm.spice': 'Reishi x Salz',
      'functional.calm.benefits': 'Clean Label. 2 Zutaten.',
      'functional.calm.description': 'Sanfte Angstlinderung. Sanfte Übergänge.',
      'functional.rest.name': 'ERHOLUNG',
      'functional.rest.subtitle': 'Gut schlafen',
      'functional.rest.spice': 'Geräuchertes Salz',
      'functional.rest.benefits': 'Reishi, Baldrian, Magnesium',
      'functional.rest.description': 'Tiefe Ruhe. Qualitätsschlaf.',
      'functional.waitlist.title': 'Auf die Warteliste',
      'functional.waitlist.description': 'Sichern Sie sich als Erste unsere adaptogene Gewürzreihe. Treten Sie der Warteliste bei für frühen Zugang beim Start in UK- und EU-Märkten.',
      'functional.waitlist.note': 'Begrenzter früher Zugang. Keine Zahlung erforderlich',
      'functional.waitlist.placeholder': 'ihre@email.de',
      'functional.waitlist.cta': 'Beitreten',
      'functional.waitlist.success': '✓ Sie stehen auf der Liste',
      'howItWorks.title': 'So funktioniert es',
      'howItWorks.subtitle': 'Einfach. Bedeutungsvoll. Wirkungsvoll.',
      'howItWorks.step1.title': 'Auf die Warteliste',
      'howItWorks.step1.description': 'Treten Sie der Warteliste bei für frühen Zugang zu unserer adaptogenen Gewürzreihe. Keine Zahlung erforderlich.',
      'howItWorks.step2.title': 'Würzen Sie Ihre Mahlzeiten',
      'howItWorks.step2.description': 'Fügen Sie funktionalen Geschmack zu Ihrem täglichen Kochen hinzu. Unterstützen Sie Ihr mentales Wohlbefinden mit jeder Mahlzeit.',
      'howItWorks.step3.title': 'Finanzieren Sie Therapiezugang',
      'howItWorks.step3.description': 'Jede 100 verkauften Packungen werden eine Stunde Therapie für jemanden finanzieren, der sie braucht. Geschmack, der zurückgibt.',
      'howItWorks.closing.title': 'Bessere psychische Gesundheit beginnt in Ihrer Küche.',
      'howItWorks.closing.subtitle': 'Jede Mahlzeit ist eine Gelegenheit, für sich selbst und andere zu sorgen.',
      'comingSoon.coach.badge': 'Demnächst',
      'comingSoon.coach.title': 'Ihr KI-Coach',
      'comingSoon.coach.description': 'Sanfte tägliche Unterstützung zwischen Therapiesitzungen. Verfolgen Sie Ihre Stimmung, erhalten Sie personalisierte Ernährungsanstöße und bereiten Sie Erkenntnisse für Ihren Therapeuten vor.',
      'comingSoon.coach.form.title': 'Beta beitreten',
      'comingSoon.coach.form.placeholder': 'ihre@email.de',
      'comingSoon.coach.form.cta': 'Warteliste beitreten',
      'comingSoon.coach.form.success': '✓ Sie sind auf der Liste',
      'comingSoon.coach.feature1.title': 'Tägliche Check-ins',
      'comingSoon.coach.feature1.description': 'Schnelle Stimmungsverfolgung, die Ihre Muster und Rhythmen lernt.',
      'comingSoon.coach.feature2.title': 'Mikro-Aktionen',
      'comingSoon.coach.feature2.description': 'Kleine, machbare Ernährungsvorschläge, angepasst an Ihr Befinden.',
      'comingSoon.coach.feature3.title': 'Therapievorbereitung',
      'comingSoon.coach.feature3.description': 'Strukturierte Erkenntnisse für Ihre nächste Sitzung.',
      'comingSoon.fund.badge': 'Demnächst',
      'comingSoon.fund.title': 'Der Fonds',
      'comingSoon.fund.description': 'Erweiterung des Zugangs zur psychischen Gesundheitsversorgung. Beantragen Sie finanzierte Therapiesitzungen oder tragen Sie bei, um anderen den Zugang zur benötigten Unterstützung zu ermöglichen.',
      'comingSoon.fund.form.title': 'Warteliste beitreten',
      'comingSoon.fund.form.placeholder': 'ihre@email.de',
      'comingSoon.fund.form.cta': 'Benachrichtigung erhalten',
      'comingSoon.fund.form.success': '✓ Sie sind auf der Liste',
      'comingSoon.fund.feature1.title': 'Unterstützung beantragen',
      'comingSoon.fund.feature1.description': 'Zugang zu finanzierten Therapiesitzungen, wenn Sie sie am meisten brauchen.',
      'comingSoon.fund.feature2.title': 'Gemeinschaftsgetrieben',
      'comingSoon.fund.feature2.description': 'Jeder Kauf trägt bei. Jeder Beitrag zählt.',
      'comingSoon.fund.feature3.title': 'Transparente Wirkung',
      'comingSoon.fund.feature3.description': 'Sehen Sie genau, wie Ihre Unterstützung echten Menschen hilft, Zugang zu Pflege zu erhalten.',
      'roadmap.badge': 'Was kommt als Nächstes',
      'roadmap.title': 'Über die Gewürze hinaus',
      'roadmap.subtitle': 'Sobald wir für unsere ersten Wartelistenmitglieder starten, werden wir zwei weitere Programme eröffnen.',
      'roadmap.coach.title': 'KI-Wellness-Coach',
      'roadmap.coach.description': 'Sanfte tägliche Unterstützung zwischen Therapiesitzungen. Stimmungsverfolgung, Ernährungstipps und Therapievorbereitung.',
      'roadmap.coach.status': 'Öffnet nach dem Launch',
      'roadmap.fund.title': 'Der Therapie-Fonds',
      'roadmap.fund.description': 'Alle 100 Packungen finanzieren eine Stunde Therapie. Beantragen Sie finanzierte Sitzungen oder tragen Sie bei.',
      'roadmap.fund.status': 'Öffnet nach dem Launch',
      'roadmap.note': 'Treten Sie heute der Warteliste bei, um beide freizuschalten.',
      'social.title': 'Folgen Sie der Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Mit Vernunft würzen',
      'footer.subtitle': 'Geschmack bekommen. Therapie schenken.',
      'footer.cta.waitlist': 'Warteliste',
      'footer.cta.coach': 'Beta beitreten',
      'footer.cta.fund': 'Warteliste beitreten',
      'floating.text': 'Treten Sie der Neurolution bei'
    },
    pt: {
      'nav.home': 'Início',
      'nav.whyMushrooms': 'Porque Isto Importa',
      'nav.spices': 'Especiarias Funcionais',
      'nav.aiCoach': 'Coach IA',
      'nav.howItWorks': 'Como Funciona',
      'nav.community': 'Comunidade',
      'nav.joinWaitlist': 'Lista de Espera',
      'nav.getCoach': 'Obter Coach',
      'hero.badge': 'Em Breve',
      'hero.tagline': 'Tempere com Razão',
      'hero.hook': 'Suplementos exigem disciplina. Especiarias só precisam de jantar.',
      'hero.subtitle': 'A primeira gama de especiarias adaptogénicas do mundo. Três misturas de rótulo limpo que apoiam o seu bem-estar mental. Sem comprimidos, sem pós, sem passos extra.',
      'hero.impact': 'Cada 100 pacotes irão financiar uma hora de terapia.',
      'hero.cta.primary': 'Entrar na Lista de Espera',
      'hero.image.alt': 'Mão polvilhando NeuroSpice num wok com vegetais frescos numa cozinha moderna',
      'problem.title': 'Porque Isto Importa',
      'problem.subtitle': 'O apoio à saúde mental deve ser acessível a todos. Estamos a construir um modelo onde cada compra cria impacto.',
      'problem.stat1.value': '1 em 5',
      'problem.stat1.label': 'Adultos experienciam doença mental anualmente',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Relatam que a nutrição impacta a sua saúde mental',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Custo médio mensal de terapia na Europa',
      'problem.impact.title': 'O apoio à saúde mental não deve ser um luxo.',
      'problem.impact.text': 'Cada 100 pacotes NeuroSpice vendidos irão financiar uma hora de terapia para alguém que não a pode pagar. Sabor que retribui.',
      'problem.cta': 'Entrar na Lista de Espera',
      'useCases.badge': 'Em Acção',
      'useCases.title': 'Integrado no Seu Dia.',
      'useCases.subtitle': 'Não é uma rotina. Não é um ritual. Apenas versões melhores do que já faz.',
      'useCases.case1.tag': 'Pequeno-almoço',
      'useCases.case1.product': 'Mistura Energia · Cordyceps x Pimentão',
      'useCases.case1.title': 'Pequeno-Almoço, Aceso.',
      'useCases.case1.description': 'Uma pitada sobre shakshuka transforma o pequeno-almoço num impulso de energia. Cordyceps e pimentão. Direto na frigideira.',
      'useCases.case2.tag': 'Hora do Cocktail',
      'useCases.case2.product': 'Mistura Descanso · Reishi x Sal',
      'useCases.case2.title': 'Tempere o Copo. Descontraia.',
      'useCases.case2.description': 'Bartenders usam a Mistura Descanso para temperar copos e desacelerar a noite. Reishi e sal, em cada gole.',
      'useCases.b2b.tag': 'Para Bares & Restaurantes',
      'useCases.b2b.title': 'Tem um Espaço?',
      'useCases.b2b.description': 'Preços por grosso, misturas personalizadas e storytelling no menu. Falemos.',
      'useCases.b2b.cta': 'Entrar em Contacto',
      'subscription.badge': 'A Gama',
      'subscription.title': 'Escolha o Seu Pack.',
      'subscription.subtitle': 'Single, Duo ou o Poly-Pack completo. Quanto mais tem em casa, menos paga por dose.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Uma mistura à sua escolha.',
      'subscription.bundle.single.contents': '1 especiaria',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Duas misturas. Combine o seu dia.',
      'subscription.bundle.duo.contents': '2 especiarias',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Todas as três: Foco, Energia & Descanso.',
      'subscription.bundle.poly.contents': '3 especiarias',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Melhor Valor',
      'subscription.perServing.label': 'por dose',
      'subscription.subscribe': 'Subscrever & Poupar',
      'subscription.subscribe.description': 'Entrega mensal, cancele a qualquer momento.',
      'subscription.save': 'Poupe 15%',
      'subscription.cta': 'Entrar na Lista de Espera',
      'subscription.note': 'Sem pagamento agora. Avisamos no lançamento.',
      'subscription.impact': 'Cada caixa financia acesso a terapia.',
      'comparison.badge': 'A Maneira Mais Inteligente',
      'comparison.title': 'Por Que Especiarias Superam Suplementos',
      'comparison.subtitle': 'Sem novos hábitos. Sem passos extra. Apenas refeições melhores.',
      'comparison.col.capsules': 'Cápsulas',
      'comparison.col.gums': 'Gomas',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Requer novo hábito',
      'comparison.row2': 'Integra-se nas refeições',
      'comparison.row3': 'Consistência diária',
      'comparison.row4': 'Agradável de tomar',
      'comparison.row5': 'Financia acesso a terapia',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Difícil',
      'comparison.val.medium': 'Médio',
      'comparison.val.effortless': 'Sem Esforço',
      'comparison.val.okay': 'Ok',
      'functional.badge': 'Primeiras Especiarias Adaptogénicas do Mundo. Em Breve',
      'functional.title': 'Gama de Especiarias Funcionais',
      'functional.subtitle': 'Três misturas de rótulo limpo. Dois ingredientes cada: um adaptogénio, uma especiaria.',
      'functional.pending': 'Pré-venda do suplemento em especiaria no Bikini Berlin, 9 e 10 de outubro, no âmbito do',
      'functional.presale.link': 'House of Food da Berlin Food Week',
      'functional.focus.name': 'FOCO',
      'functional.focus.subtitle': 'Ritual Matinal',
      'functional.focus.spice': 'Juba de Leão x Canela',
      'functional.focus.benefits': 'Rótulo limpo. 2 ingredientes.',
      'functional.focus.description': 'Clareza mental. Atenção sustentada.',
      'functional.energy.name': 'ENERGIA',
      'functional.energy.subtitle': 'Poder Diário',
      'functional.energy.spice': 'Cordyceps x Pimentão',
      'functional.energy.benefits': 'Rótulo limpo. 2 ingredientes.',
      'functional.energy.description': 'Poder bruto. Momentum natural.',
      'functional.calm.name': 'DESCANSO',
      'functional.calm.subtitle': 'Descontrair',
      'functional.calm.spice': 'Reishi x Sal',
      'functional.calm.benefits': 'Rótulo limpo. 2 ingredientes.',
      'functional.calm.description': 'Alívio suave da ansiedade. Transições suaves.',
      'functional.rest.name': 'DESCANSO',
      'functional.rest.subtitle': 'Dormir Bem',
      'functional.rest.spice': 'Sal Fumado',
      'functional.rest.benefits': 'Reishi, Valeriana, Magnésio',
      'functional.rest.description': 'Descanso profundo. Sono de qualidade.',
      'functional.waitlist.title': 'Entrar na Lista de Espera',
      'functional.waitlist.description': 'Seja o primeiro a receber a nossa gama de especiarias adaptogénicas. Entre na lista de espera para acesso antecipado quando lançarmos nos mercados UK e EU.',
      'functional.waitlist.note': 'Acesso antecipado limitado. Sem pagamento necessário',
      'functional.waitlist.placeholder': 'seu@email.com',
      'functional.waitlist.cta': 'Entrar',
      'functional.waitlist.success': '✓ Está na lista',
      'howItWorks.title': 'Como Funciona',
      'howItWorks.subtitle': 'Simples. Significativo. Impactante.',
      'howItWorks.step1.title': 'Entrar na Lista de Espera',
      'howItWorks.step1.description': 'Entre na lista de espera para acesso antecipado à nossa gama de especiarias adaptogénicas. Sem pagamento necessário.',
      'howItWorks.step2.title': 'Tempere as Suas Refeições',
      'howItWorks.step2.description': 'Adicione sabor funcional à sua cozinha diária. Apoie o seu bem-estar mental com cada refeição.',
      'howItWorks.step3.title': 'Financie Acesso a Terapia',
      'howItWorks.step3.description': 'Cada 100 pacotes vendidos irão financiar uma hora de terapia para alguém que precisa. Sabor que retribui.',
      'howItWorks.closing.title': 'Melhor saúde mental começa na sua cozinha.',
      'howItWorks.closing.subtitle': 'Cada refeição é uma oportunidade de cuidar de si e dos outros.',
      'comingSoon.coach.badge': 'Em Breve',
      'comingSoon.coach.title': 'O Seu Coach IA',
      'comingSoon.coach.description': 'Apoio diário gentil entre sessões de terapia. Acompanhe o seu humor, receba incentivos nutricionais personalizados e prepare insights para o seu terapeuta.',
      'comingSoon.coach.form.title': 'Junte-se à Beta',
      'comingSoon.coach.form.placeholder': 'seu@email.com',
      'comingSoon.coach.form.cta': 'Juntar-se à Lista',
      'comingSoon.coach.form.success': '✓ Está na lista',
      'comingSoon.coach.feature1.title': 'Check-ins Diários',
      'comingSoon.coach.feature1.description': 'Acompanhamento rápido de humor que aprende os seus padrões e ritmos.',
      'comingSoon.coach.feature2.title': 'Micro-Ações',
      'comingSoon.coach.feature2.description': 'Pequenas sugestões nutricionais adaptadas a como se sente.',
      'comingSoon.coach.feature3.title': 'Preparação para Terapia',
      'comingSoon.coach.feature3.description': 'Insights estruturados para levar à sua próxima sessão.',
      'comingSoon.fund.badge': 'Em Breve',
      'comingSoon.fund.title': 'O Fundo',
      'comingSoon.fund.description': 'Expandindo o acesso aos cuidados de saúde mental. Candidate-se a sessões de terapia financiadas ou contribua para ajudar outros a aceder ao apoio de que precisam.',
      'comingSoon.fund.form.title': 'Juntar-se à Lista de Espera',
      'comingSoon.fund.form.placeholder': 'seu@email.com',
      'comingSoon.fund.form.cta': 'Ser Notificado',
      'comingSoon.fund.form.success': '✓ Está na lista',
      'comingSoon.fund.feature1.title': 'Candidatar-se a Apoio',
      'comingSoon.fund.feature1.description': 'Aceda a sessões de terapia financiadas quando mais precisa.',
      'comingSoon.fund.feature2.title': 'Impulsionado pela Comunidade',
      'comingSoon.fund.feature2.description': 'Cada compra contribui. Cada contribuição importa.',
      'comingSoon.fund.feature3.title': 'Impacto Transparente',
      'comingSoon.fund.feature3.description': 'Veja exatamente como o seu apoio ajuda pessoas reais a aceder a cuidados.',
      'roadmap.badge': 'O Que Vem A Seguir',
      'roadmap.title': 'Para Além das Especiarias',
      'roadmap.subtitle': 'Quando lançarmos para os primeiros membros da lista de espera, abriremos dois programas adicionais.',
      'roadmap.coach.title': 'Coach de Bem-Estar IA',
      'roadmap.coach.description': 'Apoio diário gentil entre sessões de terapia. Acompanhamento de humor, incentivos nutricionais e preparação para terapia.',
      'roadmap.coach.status': 'Abre Após o Lançamento',
      'roadmap.fund.title': 'O Fundo de Terapia',
      'roadmap.fund.description': 'Cada 100 pacotes financiam uma hora de terapia. Candidate-se a sessões financiadas ou contribua para expandir o acesso.',
      'roadmap.fund.status': 'Abre Após o Lançamento',
      'roadmap.note': 'Entre na lista de espera hoje para desbloquear ambos.',
      'social.title': 'Siga a Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Tempere com Razão',
      'footer.subtitle': 'Receba sabor. Dê terapia.',
      'footer.cta.waitlist': 'Lista de Espera',
      'footer.cta.coach': 'Juntar-se à Beta',
      'footer.cta.fund': 'Juntar-se à Lista',
      'floating.text': 'Junte-se à Neurolution'
    },
    fr: {
      'nav.home': 'Accueil',
      'nav.whyMushrooms': 'Pourquoi Cela Compte',
      'nav.spices': 'Épices Fonctionnelles',
      'nav.aiCoach': 'Coach IA',
      'nav.howItWorks': 'Comment Ça Marche',
      'nav.community': 'Communauté',
      'nav.joinWaitlist': "Liste d'Attente",
      'nav.getCoach': 'Obtenir Coach',
      'hero.badge': 'Bientôt',
      'hero.tagline': 'Assaisonnez avec Raison',
      'hero.hook': "Les compléments demandent de la discipline. Les épices ne demandent qu'un repas.",
      'hero.subtitle': "La première gamme d'épices adaptogènes au monde. Trois mélanges clean label qui soutiennent votre bien-être mental. Pas de pilules, pas de poudres, pas d'étapes supplémentaires.",
      'hero.impact': 'Chaque 100 paquets financeront une heure de thérapie.',
      'hero.cta.primary': "Rejoindre la Liste d'Attente",
      'hero.image.alt': 'Main saupoudrant NeuroSpice dans un wok avec des légumes frais dans une cuisine moderne',
      'problem.title': 'Pourquoi Cela Compte',
      'problem.subtitle': 'Le soutien en santé mentale devrait être accessible à tous. Nous construisons un modèle où chaque achat crée un impact.',
      'problem.stat1.value': '1 sur 5',
      'problem.stat1.label': 'Adultes connaissent une maladie mentale chaque année',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Rapportent que la nutrition impacte leur santé mentale',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Coût mensuel moyen de thérapie en Europe',
      'problem.impact.title': 'Le soutien en santé mentale ne devrait pas être un luxe.',
      'problem.impact.text': "Chaque 100 paquets NeuroSpice vendus financeront une heure de thérapie pour quelqu'un qui ne peut pas se le permettre. Saveur qui redonne.",
      'problem.cta': "Rejoindre la Liste d'Attente",
      'useCases.badge': 'En Action',
      'useCases.title': 'Intégré à Votre Journée.',
      'useCases.subtitle': 'Pas une routine. Pas un rituel. Juste de meilleures versions de ce que vous faites déjà.',
      'useCases.case1.tag': 'Petit-déjeuner',
      'useCases.case1.product': 'Mélange Énergie · Cordyceps x Paprika',
      'useCases.case1.title': 'Petit-Déjeuner, Relevé.',
      'useCases.case1.description': 'Une pincée sur une shakshuka transforme le petit-déjeuner en coup de boost. Cordyceps et paprika. Directement dans la poêle.',
      'useCases.case2.tag': "L'Heure du Cocktail",
      'useCases.case2.product': 'Mélange Repos · Reishi x Sel',
      'useCases.case2.title': 'Bordez le Verre. Détendez-vous.',
      'useCases.case2.description': 'Les barmen utilisent le Mélange Repos pour border les cocktails et ralentir la soirée. Reishi et sel, à chaque gorgée.',
      'useCases.b2b.tag': 'Pour Bars & Restaurants',
      'useCases.b2b.title': 'Vous gérez un Lieu?',
      'useCases.b2b.description': 'Prix en gros, mélanges personnalisés et storytelling au menu. Parlons-en.',
      'useCases.b2b.cta': 'Nous Contacter',
      'subscription.badge': 'La Gamme',
      'subscription.title': 'Choisissez Votre Pack.',
      'subscription.subtitle': 'Single, Duo ou le Poly-Pack complet. Plus vous en stockez, moins vous payez par portion.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Un seul mélange, votre choix.',
      'subscription.bundle.single.contents': '1 épice',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Deux mélanges. Variez votre journée.',
      'subscription.bundle.duo.contents': '2 épices',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Les trois: Concentration, Énergie & Repos.',
      'subscription.bundle.poly.contents': '3 épices',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Meilleure Valeur',
      'subscription.perServing.label': 'par portion',
      'subscription.subscribe': "S'abonner & Économiser",
      'subscription.subscribe.description': 'Livraison mensuelle, annulez à tout moment.',
      'subscription.save': 'Économisez 15%',
      'subscription.cta': "Rejoindre la Liste d'Attente",
      'subscription.note': 'Aucun paiement maintenant. Nous vous préviendrons au lancement.',
      'subscription.impact': "Chaque boîte finance l'accès à la thérapie.",
      'comparison.badge': 'La Façon Plus Intelligente',
      'comparison.title': 'Pourquoi les Épices Battent les Compléments',
      'comparison.subtitle': "Pas de nouvelles habitudes. Pas d'étapes supplémentaires. Juste de meilleurs repas.",
      'comparison.col.capsules': 'Gélules',
      'comparison.col.gums': 'Gommes',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Nécessite une nouvelle habitude',
      'comparison.row2': "S'intègre aux repas",
      'comparison.row3': 'Cohérence quotidienne',
      'comparison.row4': 'Agréable à prendre',
      'comparison.row5': "Finance l'accès à la thérapie",
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Difficile',
      'comparison.val.medium': 'Moyen',
      'comparison.val.effortless': 'Sans Effort',
      'comparison.val.okay': 'Correct',
      'functional.badge': 'Premières Épices Adaptogènes au Monde. Bientôt',
      'functional.title': "Gamme d'Épices Fonctionnelles",
      'functional.subtitle': 'Trois mélanges clean label. Deux ingrédients chacun: un adaptogène, une épice.',
      'functional.pending': 'Prévente du complément épicé au Bikini Berlin, les 9 et 10 octobre, dans le cadre de',
      'functional.presale.link': 'House of Food de la Berlin Food Week',
      'functional.focus.name': 'CONCENTRATION',
      'functional.focus.subtitle': 'Rituel Matinal',
      'functional.focus.spice': 'Crinière de Lion x Cannelle',
      'functional.focus.benefits': 'Clean label. 2 ingrédients.',
      'functional.focus.description': 'Clarté mentale. Attention soutenue.',
      'functional.energy.name': 'ÉNERGIE',
      'functional.energy.subtitle': 'Puissance Quotidienne',
      'functional.energy.spice': 'Cordyceps x Paprika',
      'functional.energy.benefits': 'Clean label. 2 ingrédients.',
      'functional.energy.description': 'Puissance brute. Élan naturel.',
      'functional.calm.name': 'REPOS',
      'functional.calm.subtitle': 'Détente',
      'functional.calm.spice': 'Reishi x Sel',
      'functional.calm.benefits': 'Clean label. 2 ingrédients.',
      'functional.calm.description': "Soulagement doux de l'anxiété. Transitions fluides.",
      'functional.rest.name': 'REPOS',
      'functional.rest.subtitle': 'Bien Dormir',
      'functional.rest.spice': 'Sel Fumé',
      'functional.rest.benefits': 'Reishi, Valériane, Magnésium',
      'functional.rest.description': 'Repos profond. Sommeil de qualité.',
      'functional.waitlist.title': "Rejoindre la Liste d'Attente",
      'functional.waitlist.description': "Soyez le premier à recevoir notre gamme d'épices adaptogènes. Rejoignez la liste d'attente pour un accès anticipé lors du lancement sur les marchés UK et EU.",
      'functional.waitlist.note': 'Accès anticipé limité. Aucun paiement requis',
      'functional.waitlist.placeholder': 'votre@email.com',
      'functional.waitlist.cta': 'Rejoindre',
      'functional.waitlist.success': '✓ Vous êtes sur la liste',
      'howItWorks.title': 'Comment Ça Marche',
      'howItWorks.subtitle': 'Simple. Significatif. Impactant.',
      'howItWorks.step1.title': "Rejoindre la Liste d'Attente",
      'howItWorks.step1.description': "Rejoignez la liste d'attente pour un accès anticipé à notre gamme d'épices adaptogènes. Aucun paiement requis.",
      'howItWorks.step2.title': 'Assaisonnez Vos Repas',
      'howItWorks.step2.description': 'Ajoutez une saveur fonctionnelle à votre cuisine quotidienne. Soutenez votre bien-être mental à chaque repas.',
      'howItWorks.step3.title': "Financez l'Accès à la Thérapie",
      'howItWorks.step3.description': "Chaque 100 paquets vendus financeront une heure de thérapie pour quelqu'un qui en a besoin. Saveur qui redonne.",
      'howItWorks.closing.title': 'Une meilleure santé mentale commence dans votre cuisine.',
      'howItWorks.closing.subtitle': 'Chaque repas est une opportunité de prendre soin de vous et des autres.',
      'comingSoon.coach.badge': 'Bientôt',
      'comingSoon.coach.title': 'Votre Coach IA',
      'comingSoon.coach.description': 'Soutien quotidien doux entre les séances de thérapie. Suivez votre humeur, recevez des suggestions nutritionnelles personnalisées et préparez des insights pour votre thérapeute.',
      'comingSoon.coach.form.title': 'Rejoindre la Bêta',
      'comingSoon.coach.form.placeholder': 'votre@email.com',
      'comingSoon.coach.form.cta': 'Rejoindre la Liste',
      'comingSoon.coach.form.success': '✓ Vous êtes sur la liste',
      'comingSoon.coach.feature1.title': 'Check-ins Quotidiens',
      'comingSoon.coach.feature1.description': "Suivi rapide de l'humeur qui apprend vos modèles et rythmes.",
      'comingSoon.coach.feature2.title': 'Micro-Actions',
      'comingSoon.coach.feature2.description': 'Petites suggestions nutritionnelles réalisables adaptées à votre ressenti.',
      'comingSoon.coach.feature3.title': 'Préparation Thérapie',
      'comingSoon.coach.feature3.description': 'Insights structurés à apporter à votre prochaine séance.',
      'comingSoon.fund.badge': 'Bientôt',
      'comingSoon.fund.title': 'Le Fonds',
      'comingSoon.fund.description': "Élargir l'accès aux soins de santé mentale. Postulez pour des séances de thérapie financées ou contribuez pour aider d'autres à accéder au soutien dont ils ont besoin.",
      'comingSoon.fund.form.title': "Rejoindre la Liste d'Attente",
      'comingSoon.fund.form.placeholder': 'votre@email.com',
      'comingSoon.fund.form.cta': 'Être Notifié',
      'comingSoon.fund.form.success': '✓ Vous êtes sur la liste',
      'comingSoon.fund.feature1.title': 'Postuler pour du Soutien',
      'comingSoon.fund.feature1.description': 'Accédez à des séances de thérapie financiées quand vous en avez le plus besoin.',
      'comingSoon.fund.feature2.title': 'Propulsé par la Communauté',
      'comingSoon.fund.feature2.description': 'Chaque achat contribue. Chaque contribution compte.',
      'comingSoon.fund.feature3.title': 'Impact Transparent',
      'comingSoon.fund.feature3.description': 'Voyez exactement comment votre soutien aide de vraies personnes à accéder aux soins.',
      'roadmap.badge': 'La Suite',
      'roadmap.title': 'Au-delà des Épices',
      'roadmap.subtitle': "Une fois le lancement effectué pour nos premiers membres de la liste d'attente, nous ouvrirons deux programmes supplémentaires.",
      'roadmap.coach.title': 'Coach Bien-être IA',
      'roadmap.coach.description': "Soutien quotidien doux entre les séances de thérapie. Suivi de l'humeur, conseils nutritionnels et préparation thérapie.",
      'roadmap.coach.status': 'Ouvre Après le Lancement',
      'roadmap.fund.title': 'Le Fonds de Thérapie',
      'roadmap.fund.description': "Chaque 100 paquets finance une heure de thérapie. Postulez pour des séances financées ou contribuez à élargir l'accès.",
      'roadmap.fund.status': 'Ouvre Après le Lancement',
      'roadmap.note': "Rejoignez la liste d'attente aujourd'hui pour débloquer les deux.",
      'social.title': 'Suivez la Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Assaisonnez avec Raison',
      'footer.subtitle': 'Recevez la saveur. Offrez la thérapie.',
      'footer.cta.waitlist': "Liste d'Attente",
      'footer.cta.coach': 'Rejoindre Bêta',
      'footer.cta.fund': 'Rejoindre la Liste',
      'floating.text': 'Rejoignez la Neurolution'
    },
    es: {
      'nav.home': 'Inicio',
      'nav.whyMushrooms': 'Por Qué Importa',
      'nav.spices': 'Especias Funcionales',
      'nav.aiCoach': 'Coach IA',
      'nav.howItWorks': 'Cómo Funciona',
      'nav.community': 'Comunidad',
      'nav.joinWaitlist': 'Lista de Espera',
      'nav.getCoach': 'Obtener Coach',
      'hero.badge': 'Próximamente',
      'hero.tagline': 'Sazona con Razón',
      'hero.hook': 'Los suplementos necesitan disciplina. Las especias solo necesitan una comida.',
      'hero.subtitle': 'La primera gama de especias adaptogénicas del mundo. Tres mezclas de etiqueta limpia que apoyan tu bienestar mental. Sin pastillas, sin polvos, sin pasos extra.',
      'hero.impact': 'Cada 100 paquetes financiarán una hora de terapia.',
      'hero.cta.primary': 'Unirse a la Lista de Espera',
      'hero.image.alt': 'Mano espolvoreando NeuroSpice en un wok con verduras frescas en una cocina moderna',
      'problem.title': 'Por Qué Importa',
      'problem.subtitle': 'El apoyo a la salud mental debería ser accesible para todos. Estamos construyendo un modelo donde cada compra crea impacto.',
      'problem.stat1.value': '1 de cada 5',
      'problem.stat1.label': 'Adultos experimentan enfermedad mental anualmente',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Reportan que la nutrición impacta su salud mental',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Costo mensual promedio de terapia en Europa',
      'problem.impact.title': 'El apoyo a la salud mental no debería ser un lujo.',
      'problem.impact.text': 'Cada 100 paquetes NeuroSpice vendidos financiarán una hora de terapia para alguien que no puede permitírselo. Sabor que devuelve.',
      'problem.cta': 'Unirse a la Lista de Espera',
      'useCases.badge': 'En Acción',
      'useCases.title': 'Integrado en Tu Día.',
      'useCases.subtitle': 'No es una rutina. No es un ritual. Solo mejores versiones de lo que ya haces.',
      'useCases.case1.tag': 'Desayuno',
      'useCases.case1.product': 'Mezcla Energía · Cordyceps x Pimentón',
      'useCases.case1.title': 'Desayuno, Encendido.',
      'useCases.case1.description': 'Una pizca sobre shakshuka convierte el desayuno en un impulso de energía. Cordyceps y pimentón. Directo a la sartén.',
      'useCases.case2.tag': 'Hora del Cóctel',
      'useCases.case2.product': 'Mezcla Descanso · Reishi x Sal',
      'useCases.case2.title': 'Sazona el Vaso. Relájate.',
      'useCases.case2.description': 'Los bartenders usan la Mezcla Descanso para bordes de cócteles y noches tranquilas. Reishi y sal, sorbo a sorbo.',
      'useCases.b2b.tag': 'Para Bares & Restaurantes',
      'useCases.b2b.title': '¿Diriges un Local?',
      'useCases.b2b.description': 'Precios al por mayor, mezclas personalizadas y storytelling en el menú. Hablemos.',
      'useCases.b2b.cta': 'Contáctanos',
      'subscription.badge': 'La Gama',
      'subscription.title': 'Elige Tu Pack.',
      'subscription.subtitle': 'Single, Duo o el Poly-Pack completo. Cuanto más almacenas, menos pagas por porción.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Una sola mezcla, tu elección.',
      'subscription.bundle.single.contents': '1 especia',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Dos mezclas. Combina tu día.',
      'subscription.bundle.duo.contents': '2 especias',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Las tres: Enfoque, Energía & Descanso.',
      'subscription.bundle.poly.contents': '3 especias',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Mejor Valor',
      'subscription.perServing.label': 'por porción',
      'subscription.subscribe': 'Suscribirse y Ahorrar',
      'subscription.subscribe.description': 'Entrega mensual, cancela en cualquier momento.',
      'subscription.save': 'Ahorra 15%',
      'subscription.cta': 'Unirse a la Lista de Espera',
      'subscription.note': 'Sin pago ahora. Te avisaremos en el lanzamiento.',
      'subscription.impact': 'Cada caja financia acceso a terapia.',
      'comparison.badge': 'La Forma Más Inteligente',
      'comparison.title': 'Por Qué las Especias Superan a los Suplementos',
      'comparison.subtitle': 'Sin nuevos hábitos. Sin pasos extra. Solo mejores comidas.',
      'comparison.col.capsules': 'Cápsulas',
      'comparison.col.gums': 'Gomas',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Requiere nuevo hábito',
      'comparison.row2': 'Se integra en las comidas',
      'comparison.row3': 'Consistencia diaria',
      'comparison.row4': 'Agradable de tomar',
      'comparison.row5': 'Financia acceso a terapia',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Difícil',
      'comparison.val.medium': 'Medio',
      'comparison.val.effortless': 'Sin Esfuerzo',
      'comparison.val.okay': 'Bien',
      'functional.badge': 'Primeras Especias Adaptogénicas del Mundo. Próximamente',
      'functional.title': 'Gama de Especias Funcionales',
      'functional.subtitle': 'Tres mezclas de etiqueta limpia. Dos ingredientes cada una: un adaptógeno, una especia.',
      'functional.pending': 'Preventa del suplemento especiado en Bikini Berlin, 9 y 10 de octubre, como parte de',
      'functional.presale.link': 'House of Food de la Berlin Food Week',
      'functional.focus.name': 'ENFOQUE',
      'functional.focus.subtitle': 'Ritual Matutino',
      'functional.focus.spice': 'Melena de León x Canela',
      'functional.focus.benefits': 'Etiqueta limpia. 2 ingredientes.',
      'functional.focus.description': 'Claridad mental. Atención sostenida.',
      'functional.energy.name': 'ENERGÍA',
      'functional.energy.subtitle': 'Poder Diario',
      'functional.energy.spice': 'Cordyceps x Pimentón',
      'functional.energy.benefits': 'Etiqueta limpia. 2 ingredientes.',
      'functional.energy.description': 'Poder bruto. Impulso natural.',
      'functional.calm.name': 'DESCANSO',
      'functional.calm.subtitle': 'Relajarse',
      'functional.calm.spice': 'Reishi x Sal',
      'functional.calm.benefits': 'Etiqueta limpia. 2 ingredientes.',
      'functional.calm.description': 'Alivio suave de la ansiedad. Transiciones suaves.',
      'functional.rest.name': 'DESCANSO',
      'functional.rest.subtitle': 'Dormir Bien',
      'functional.rest.spice': 'Sal Ahumada',
      'functional.rest.benefits': 'Reishi, Valeriana, Magnesio',
      'functional.rest.description': 'Descanso profundo. Sueño de calidad.',
      'functional.waitlist.title': 'Unirse a la Lista de Espera',
      'functional.waitlist.description': 'Sé el primero en recibir nuestra gama de especias adaptogénicas. Únete a la lista de espera para acceso anticipado cuando lancemos en mercados UK y EU.',
      'functional.waitlist.note': 'Acceso anticipado limitado. Sin pago requerido',
      'functional.waitlist.placeholder': 'tu@email.com',
      'functional.waitlist.cta': 'Unirse',
      'functional.waitlist.success': '✓ Estás en la lista',
      'howItWorks.title': 'Cómo Funciona',
      'howItWorks.subtitle': 'Simple. Significativo. Impactante.',
      'howItWorks.step1.title': 'Unirse a la Lista de Espera',
      'howItWorks.step1.description': 'Únete a la lista de espera para acceso anticipado a nuestra gama de especias adaptogénicas. Sin pago requerido.',
      'howItWorks.step2.title': 'Sazona Tus Comidas',
      'howItWorks.step2.description': 'Añade sabor funcional a tu cocina diaria. Apoya tu bienestar mental con cada comida.',
      'howItWorks.step3.title': 'Financia Acceso a Terapia',
      'howItWorks.step3.description': 'Cada 100 paquetes vendidos financiarán una hora de terapia para alguien que lo necesita. Sabor que devuelve.',
      'howItWorks.closing.title': 'Mejor salud mental comienza en tu cocina.',
      'howItWorks.closing.subtitle': 'Cada comida es una oportunidad de cuidarte a ti mismo y a otros.',
      'comingSoon.coach.badge': 'Próximamente',
      'comingSoon.coach.title': 'Tu Coach IA',
      'comingSoon.coach.description': 'Apoyo diario suave entre sesiones de terapia. Rastrea tu estado de ánimo, recibe sugerencias nutricionales personalizadas y prepara insights para tu terapeuta.',
      'comingSoon.coach.form.title': 'Unirse a la Beta',
      'comingSoon.coach.form.placeholder': 'tu@email.com',
      'comingSoon.coach.form.cta': 'Unirse a la Lista',
      'comingSoon.coach.form.success': '✓ Estás en la lista',
      'comingSoon.coach.feature1.title': 'Check-ins Diarios',
      'comingSoon.coach.feature1.description': 'Seguimiento rápido del estado de ánimo que aprende tus patrones y ritmos.',
      'comingSoon.coach.feature2.title': 'Micro-Acciones',
      'comingSoon.coach.feature2.description': 'Pequeñas sugerencias nutricionales adaptadas a cómo te sientes.',
      'comingSoon.coach.feature3.title': 'Preparación para Terapia',
      'comingSoon.coach.feature3.description': 'Insights estructurados para llevar a tu próxima sesión.',
      'comingSoon.fund.badge': 'Próximamente',
      'comingSoon.fund.title': 'El Fondo',
      'comingSoon.fund.description': 'Expandiendo el acceso a la atención de salud mental. Solicita sesiones de terapia financiadas o contribuye para ayudar a otros a acceder al apoyo que necesitan.',
      'comingSoon.fund.form.title': 'Unirse a la Lista de Espera',
      'comingSoon.fund.form.placeholder': 'tu@email.com',
      'comingSoon.fund.form.cta': 'Ser Notificado',
      'comingSoon.fund.form.success': '✓ Estás en la lista',
      'comingSoon.fund.feature1.title': 'Solicitar Apoyo',
      'comingSoon.fund.feature1.description': 'Accede a sesiones de terapia financiadas cuando más las necesitas.',
      'comingSoon.fund.feature2.title': 'Impulsado por la Comunidad',
      'comingSoon.fund.feature2.description': 'Cada compra contribuye. Cada contribución importa.',
      'comingSoon.fund.feature3.title': 'Impacto Transparente',
      'comingSoon.fund.feature3.description': 'Ve exactamente cómo tu apoyo ayuda a personas reales a acceder a atención.',
      'roadmap.badge': 'Lo Que Viene',
      'roadmap.title': 'Más Allá de las Especias',
      'roadmap.subtitle': 'Una vez cumplamos nuestros primeros pre-pedidos, abriremos dos programas adicionales.',
      'roadmap.coach.title': 'Coach de Bienestar IA',
      'roadmap.coach.description': 'Apoyo diario suave entre sesiones de terapia. Seguimiento del estado de ánimo, sugerencias nutricionales y preparación para terapia.',
      'roadmap.coach.status': 'Abre Tras el Lanzamiento',
      'roadmap.fund.title': 'El Fondo de Terapia',
      'roadmap.fund.description': 'Cada 100 paquetes financian una hora de terapia. Solicita sesiones financiadas o contribuye para ampliar el acceso.',
      'roadmap.fund.status': 'Abre Tras el Lanzamiento',
      'roadmap.note': 'Únete a la lista de espera hoy para desbloquear ambos.',
      'social.title': 'Sigue la Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Sazona con Razón',
      'footer.subtitle': 'Recibe sabor. Da terapia.',
      'footer.cta.waitlist': 'Lista de Espera',
      'footer.cta.coach': 'Unirse a Beta',
      'footer.cta.fund': 'Unirse a la Lista',
      'floating.text': 'Únete a la Neurolution'
    },
    it: {
      'nav.home': 'Home',
      'nav.whyMushrooms': 'Perché Conta',
      'nav.spices': 'Spezie Funzionali',
      'nav.aiCoach': 'Coach IA',
      'nav.howItWorks': 'Come Funziona',
      'nav.community': 'Comunità',
      'nav.joinWaitlist': "Lista d'Attesa",
      'nav.getCoach': 'Ottieni Coach',
      'hero.badge': 'Prossimamente',
      'hero.tagline': 'Condisci con Ragione',
      'hero.hook': 'Gli integratori richiedono disciplina. Le spezie richiedono solo una cena.',
      'hero.subtitle': 'La prima gamma di spezie adattogene al mondo. Tre miscele clean label che supportano il tuo benessere mentale. Senza pillole, senza polveri, senza passaggi extra.',
      'hero.impact': "Ogni 100 confezioni finanzieranno un'ora di terapia.",
      'hero.cta.primary': "Unisciti alla Lista d'Attesa",
      'hero.image.alt': 'Mano che cosparge NeuroSpice in un wok con verdure fresche in una cucina moderna',
      'problem.title': 'Perché Conta',
      'problem.subtitle': 'Il supporto alla salute mentale dovrebbe essere accessibile a tutti. Stiamo costruendo un modello dove ogni acquisto crea impatto.',
      'problem.stat1.value': '1 su 5',
      'problem.stat1.label': 'Adulti sperimentano malattie mentali annualmente',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Riportano che la nutrizione impatta la loro salute mentale',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Costo mensile medio della terapia in Europa',
      'problem.impact.title': 'Il supporto alla salute mentale non dovrebbe essere un lusso.',
      'problem.impact.text': "Ogni 100 confezioni NeuroSpice vendute finanzieranno un'ora di terapia per qualcuno che non può permetterselo. Sapore che restituisce.",
      'problem.cta': "Unisciti alla Lista d'Attesa",
      'useCases.badge': 'In Azione',
      'useCases.title': 'Integrato nella Tua Giornata.',
      'useCases.subtitle': 'Non una routine. Non un rituale. Solo versioni migliori di ciò che già fai.',
      'useCases.case1.tag': 'Colazione',
      'useCases.case1.product': 'Miscela Energia · Cordyceps x Paprika',
      'useCases.case1.title': 'Colazione, Accesa.',
      'useCases.case1.description': 'Un pizzico sulla shakshuka trasforma la colazione in una carica di energia. Cordyceps e paprika. Dritto in padella.',
      'useCases.case2.tag': "L'Ora del Cocktail",
      'useCases.case2.product': 'Miscela Riposo · Reishi x Sale',
      'useCases.case2.title': 'Decora il Bicchiere. Rilassati.',
      'useCases.case2.description': 'I bartender usano la Miscela Riposo per i bordi dei cocktail e serate rilassate. Reishi e sale, sorso dopo sorso.',
      'useCases.b2b.tag': 'Per Bar & Ristoranti',
      'useCases.b2b.title': 'Gestisci un Locale?',
      'useCases.b2b.description': "Prezzi all'ingrosso, miscele personalizzate e storytelling nel menu. Parliamone.",
      'useCases.b2b.cta': 'Contattaci',
      'subscription.badge': 'La Gamma',
      'subscription.title': 'Scegli il Tuo Pack.',
      'subscription.subtitle': 'Single, Duo o il Poly-Pack completo. Più ne hai, meno paghi per porzione.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Una miscela, la tua scelta.',
      'subscription.bundle.single.contents': '1 spezia',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Due miscele. Varia la tua giornata.',
      'subscription.bundle.duo.contents': '2 spezie',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Tutte e tre: Concentrazione, Energia & Riposo.',
      'subscription.bundle.poly.contents': '3 spezie',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Miglior Valore',
      'subscription.perServing.label': 'a porzione',
      'subscription.subscribe': 'Iscriviti & Risparmia',
      'subscription.subscribe.description': 'Consegna mensile, cancella in qualsiasi momento.',
      'subscription.save': 'Risparmia 15%',
      'subscription.cta': "Unisciti alla Lista d'Attesa",
      'subscription.note': 'Nessun pagamento ora. Ti avviseremo al lancio.',
      'subscription.impact': 'Ogni scatola finanzia accesso alla terapia.',
      'comparison.badge': 'Il Modo Più Intelligente',
      'comparison.title': 'Perché le Spezie Battono gli Integratori',
      'comparison.subtitle': 'Nessuna nuova abitudine. Nessun passaggio extra. Solo pasti migliori.',
      'comparison.col.capsules': 'Capsule',
      'comparison.col.gums': 'Gomme',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Richiede nuova abitudine',
      'comparison.row2': 'Si integra nei pasti',
      'comparison.row3': 'Costanza quotidiana',
      'comparison.row4': 'Piacevole da assumere',
      'comparison.row5': 'Finanzia accesso alla terapia',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Difficile',
      'comparison.val.medium': 'Medio',
      'comparison.val.effortless': 'Senza Sforzo',
      'comparison.val.okay': 'Ok',
      'functional.badge': 'Prime Spezie Adattogene al Mondo. Prossimamente',
      'functional.title': 'Gamma di Spezie Funzionali',
      'functional.subtitle': 'Tre miscele clean label. Due ingredienti ciascuna: un adattogeno, una spezia.',
      'functional.pending': "Prevendita dell'integratore speziato al Bikini Berlin, 9 e 10 ottobre, nell'ambito di",
      'functional.presale.link': 'House of Food della Berlin Food Week',
      'functional.focus.name': 'CONCENTRAZIONE',
      'functional.focus.subtitle': 'Rituale Mattutino',
      'functional.focus.spice': 'Criniera di Leone x Cannella',
      'functional.focus.benefits': 'Clean label. 2 ingredienti.',
      'functional.focus.description': 'Chiarezza mentale. Attenzione sostenuta.',
      'functional.energy.name': 'ENERGIA',
      'functional.energy.subtitle': 'Potenza Giornaliera',
      'functional.energy.spice': 'Cordyceps x Paprika',
      'functional.energy.benefits': 'Clean label. 2 ingredienti.',
      'functional.energy.description': 'Potenza grezza. Slancio naturale.',
      'functional.calm.name': 'RIPOSO',
      'functional.calm.subtitle': 'Rilassarsi',
      'functional.calm.spice': 'Reishi x Sale',
      'functional.calm.benefits': 'Clean label. 2 ingredienti.',
      'functional.calm.description': "Sollievo delicato dall'ansia. Transizioni fluide.",
      'functional.rest.name': 'RIPOSO',
      'functional.rest.subtitle': 'Dormire Bene',
      'functional.rest.spice': 'Sale Affumicato',
      'functional.rest.benefits': 'Reishi, Valeriana, Magnesio',
      'functional.rest.description': 'Riposo profondo. Sonno di qualità.',
      'functional.waitlist.title': "Unisciti alla Lista d'Attesa",
      'functional.waitlist.description': "Sii il primo a ricevere la nostra gamma di spezie adattogene. Unisciti alla lista d'attesa per accesso anticipato quando lanceremo nei mercati UK e EU.",
      'functional.waitlist.note': 'Accesso anticipato limitato. Nessun pagamento richiesto',
      'functional.waitlist.placeholder': 'tua@email.com',
      'functional.waitlist.cta': 'Unisciti',
      'functional.waitlist.success': '✓ Sei nella lista',
      'howItWorks.title': 'Come Funziona',
      'howItWorks.subtitle': 'Semplice. Significativo. Impattante.',
      'howItWorks.step1.title': "Unisciti alla Lista d'Attesa",
      'howItWorks.step1.description': "Unisciti alla lista d'attesa per accesso anticipato alla nostra gamma di spezie adattogene. Nessun pagamento richiesto.",
      'howItWorks.step2.title': 'Condisci i Tuoi Pasti',
      'howItWorks.step2.description': 'Aggiungi sapore funzionale alla tua cucina quotidiana. Supporta il tuo benessere mentale ad ogni pasto.',
      'howItWorks.step3.title': "Finanzia l'Accesso alla Terapia",
      'howItWorks.step3.description': "Ogni 100 confezioni vendute finanzieranno un'ora di terapia per qualcuno che ne ha bisogno. Sapore che restituisce.",
      'howItWorks.closing.title': 'Una migliore salute mentale inizia nella tua cucina.',
      'howItWorks.closing.subtitle': "Ogni pasto è un'opportunità per prenderti cura di te stesso e degli altri.",
      'comingSoon.coach.badge': 'Prossimamente',
      'comingSoon.coach.title': 'Il Tuo Coach IA',
      'comingSoon.coach.description': 'Supporto quotidiano delicato tra le sessioni di terapia. Traccia il tuo umore, ricevi suggerimenti nutrizionali personalizzati e prepara insights per il tuo terapeuta.',
      'comingSoon.coach.form.title': 'Iscriviti alla Beta',
      'comingSoon.coach.form.placeholder': 'tua@email.com',
      'comingSoon.coach.form.cta': 'Iscriviti alla Lista',
      'comingSoon.coach.form.success': '✓ Sei nella lista',
      'comingSoon.coach.feature1.title': 'Check-in Giornalieri',
      'comingSoon.coach.feature1.description': "Tracciamento rapido dell'umore che impara i tuoi schemi e ritmi.",
      'comingSoon.coach.feature2.title': 'Micro-Azioni',
      'comingSoon.coach.feature2.description': 'Piccoli suggerimenti nutrizionali adattati a come ti senti.',
      'comingSoon.coach.feature3.title': 'Preparazione Terapia',
      'comingSoon.coach.feature3.description': 'Insights strutturati da portare alla tua prossima sessione.',
      'comingSoon.fund.badge': 'Prossimamente',
      'comingSoon.fund.title': 'Il Fondo',
      'comingSoon.fund.description': "Espandendo l'accesso alle cure per la salute mentale. Richiedi sessioni di terapia finanziate o contribuisci per aiutare altri ad accedere al supporto di cui hanno bisogno.",
      'comingSoon.fund.form.title': "Iscriviti alla Lista d'Attesa",
      'comingSoon.fund.form.placeholder': 'tua@email.com',
      'comingSoon.fund.form.cta': 'Ricevi Notifica',
      'comingSoon.fund.form.success': '✓ Sei nella lista',
      'comingSoon.fund.feature1.title': 'Richiedi Supporto',
      'comingSoon.fund.feature1.description': 'Accedi a sessioni di terapia finanziate quando ne hai più bisogno.',
      'comingSoon.fund.feature2.title': 'Alimentato dalla Comunità',
      'comingSoon.fund.feature2.description': 'Ogni acquisto contribuisce. Ogni contributo conta.',
      'comingSoon.fund.feature3.title': 'Impatto Trasparente',
      'comingSoon.fund.feature3.description': 'Vedi esattamente come il tuo supporto aiuta persone reali ad accedere alle cure.',
      'roadmap.badge': 'Cosa Arriva',
      'roadmap.title': 'Oltre le Spezie',
      'roadmap.subtitle': "Una volta lanciato per i nostri primi membri della lista d'attesa, apriremo due programmi aggiuntivi.",
      'roadmap.coach.title': 'Coach Benessere IA',
      'roadmap.coach.description': "Supporto quotidiano delicato tra le sessioni di terapia. Tracciamento dell'umore, suggerimenti nutrizionali e preparazione alla terapia.",
      'roadmap.coach.status': 'Apre Dopo il Lancio',
      'roadmap.fund.title': 'Il Fondo Terapia',
      'roadmap.fund.description': "Ogni 100 confezioni finanziano un'ora di terapia. Richiedi sessioni finanziate o contribuisci ad ampliare l'accesso.",
      'roadmap.fund.status': 'Apre Dopo il Lancio',
      'roadmap.note': "Unisciti alla lista d'attesa oggi per sbloccare entrambi.",
      'social.title': 'Segui la Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Condisci con Ragione',
      'footer.subtitle': 'Ricevi sapore. Dona terapia.',
      'footer.cta.waitlist': "Lista d'Attesa",
      'footer.cta.coach': 'Iscriviti Beta',
      'footer.cta.fund': 'Iscriviti alla Lista',
      'floating.text': 'Unisciti alla Neurolution'
    },
    nl: {
      'nav.home': 'Home',
      'nav.whyMushrooms': 'Waarom Dit Belangrijk Is',
      'nav.spices': 'Functionele Specerijen',
      'nav.aiCoach': 'AI Coach',
      'nav.howItWorks': 'Hoe Het Werkt',
      'nav.community': 'Gemeenschap',
      'nav.joinWaitlist': 'Wachtlijst',
      'nav.getCoach': 'Krijg Coach',
      'hero.badge': 'Binnenkort',
      'hero.tagline': 'Kruid met Reden',
      'hero.hook': 'Supplementen vragen discipline. Specerijen vragen alleen een maaltijd.',
      'hero.subtitle': 'De eerste adaptogene specerijenreeks ter wereld. Drie clean label mengsels die je mentale welzijn ondersteunen. Geen pillen, geen poeders, geen extra stappen.',
      'hero.impact': 'Elke 100 verpakkingen financieren één uur therapie.',
      'hero.cta.primary': 'Inschrijven op de Wachtlijst',
      'hero.image.alt': 'Hand die NeuroSpice strooit in een wok met verse groenten in een moderne keuken',
      'problem.title': 'Waarom Dit Belangrijk Is',
      'problem.subtitle': 'Ondersteuning voor mentale gezondheid moet voor iedereen toegankelijk zijn. We bouwen een model waarbij elke aankoop impact creëert.',
      'problem.stat1.value': '1 op 5',
      'problem.stat1.label': 'Volwassenen ervaren jaarlijks psychische ziekte',
      'problem.stat2.value': '60%',
      'problem.stat2.label': 'Rapporteert dat voeding hun mentale gezondheid beïnvloedt',
      'problem.stat3.value': '€200+',
      'problem.stat3.label': 'Gemiddelde maandelijkse therapiekosten in Europa',
      'problem.impact.title': 'Ondersteuning voor mentale gezondheid mag geen luxe zijn.',
      'problem.impact.text': 'Elke 100 verkochte NeuroSpice-verpakkingen financieren één uur therapie voor iemand die het zich niet kan veroorloven. Smaak die teruggeeft.',
      'problem.cta': 'Inschrijven op de Wachtlijst',
      'useCases.badge': 'In de Praktijk',
      'useCases.title': 'Ingebouwd in Je Dag.',
      'useCases.subtitle': 'Geen routine. Geen ritueel. Gewoon betere versies van wat je al doet.',
      'useCases.case1.tag': 'Ontbijt',
      'useCases.case1.product': 'Energie-Mengsel · Cordyceps x Paprika',
      'useCases.case1.title': 'Ontbijt, Aangewakkerd.',
      'useCases.case1.description': 'Een snufje over shakshuka maakt van je ontbijt een energieboost. Cordyceps en paprika. Recht de pan in.',
      'useCases.case2.tag': 'Cocktailuur',
      'useCases.case2.product': 'Herstel-Mengsel · Reishi x Zout',
      'useCases.case2.title': 'Rand het Glas. Ontspan.',
      'useCases.case2.description': 'Bartenders gebruiken het Herstel-Mengsel voor de randen van cocktails en rustige avonden. Reishi en zout, slok voor slok.',
      'useCases.b2b.tag': 'Voor Bars & Restaurants',
      'useCases.b2b.title': 'Heb je een Zaak?',
      'useCases.b2b.description': 'Groothandelsprijzen, op maat gemaakte mengsels en menu-storytelling. Laten we praten.',
      'useCases.b2b.cta': 'Neem Contact Op',
      'subscription.badge': 'Het Assortiment',
      'subscription.title': 'Kies Je Pack.',
      'subscription.subtitle': 'Single, Duo of de complete Poly-Pack. Hoe meer je hebt, hoe minder je per portie betaalt.',
      'subscription.bundle.single.name': 'Single',
      'subscription.bundle.single.tagline': 'Eén mengsel, jouw keuze.',
      'subscription.bundle.single.contents': '1 specerij',
      'subscription.bundle.single.price': '€10.99',
      'subscription.bundle.single.perServing': '€0.36',
      'subscription.bundle.duo.name': 'Duo',
      'subscription.bundle.duo.tagline': 'Twee mengsels. Mix je dag.',
      'subscription.bundle.duo.contents': '2 specerijen',
      'subscription.bundle.duo.price': '€17.99',
      'subscription.bundle.duo.perServing': '€0.30',
      'subscription.bundle.poly.name': 'Poly-Pack',
      'subscription.bundle.poly.tagline': 'Alle drie: Focus, Energie & Herstel.',
      'subscription.bundle.poly.contents': '3 specerijen',
      'subscription.bundle.poly.price': '€24.99',
      'subscription.bundle.poly.perServing': '€0.28',
      'subscription.bundle.poly.badge': 'Beste Waarde',
      'subscription.perServing.label': 'per portie',
      'subscription.subscribe': 'Abonneren & Besparen',
      'subscription.subscribe.description': 'Maandelijkse levering, annuleer op elk moment.',
      'subscription.save': 'Bespaar 15%',
      'subscription.cta': 'Inschrijven op de Wachtlijst',
      'subscription.note': 'Geen betaling nu. We mailen je bij de lancering.',
      'subscription.impact': 'Elke box financiert toegang tot therapie.',
      'comparison.badge': 'De Slimmere Manier',
      'comparison.title': 'Waarom Specerijen Beter Zijn Dan Supplementen',
      'comparison.subtitle': 'Geen nieuwe gewoontes. Geen extra stappen. Gewoon betere maaltijden.',
      'comparison.col.capsules': 'Capsules',
      'comparison.col.gums': 'Gummies',
      'comparison.col.neurospice': 'NeuroSpice',
      'comparison.row1': 'Vereist nieuwe gewoonte',
      'comparison.row2': 'Integreert in maaltijden',
      'comparison.row3': 'Dagelijkse consistentie',
      'comparison.row4': 'Aangenaam om te nemen',
      'comparison.row5': 'Financiert toegang tot therapie',
      'comparison.val.yes': '✓',
      'comparison.val.no': '✗',
      'comparison.val.hard': 'Moeilijk',
      'comparison.val.medium': 'Gemiddeld',
      'comparison.val.effortless': 'Moeiteloos',
      'comparison.val.okay': 'Oké',
      'functional.badge': 'Werelds Eerste Adaptogene Specerijen. Binnenkort',
      'functional.title': 'Functionele Specerijen Reeks',
      'functional.subtitle': 'Drie clean label mengsels. Elk twee ingrediënten: één adaptogeen, één specerij.',
      'functional.pending': 'Voorverkoop van ons gekruide supplement in Bikini Berlin, 9 & 10 oktober, als onderdeel van',
      'functional.presale.link': 'House of Food tijdens Berlin Food Week',
      'functional.focus.name': 'FOCUS',
      'functional.focus.subtitle': 'Ochtendritueel',
      'functional.focus.spice': 'Leeuwenmanen x Kaneel',
      'functional.focus.benefits': 'Clean label. 2 ingrediënten.',
      'functional.focus.description': 'Mentale helderheid. Aanhoudende aandacht.',
      'functional.energy.name': 'ENERGIE',
      'functional.energy.subtitle': 'Dagkracht',
      'functional.energy.spice': 'Cordyceps x Paprika',
      'functional.energy.benefits': 'Clean label. 2 ingrediënten.',
      'functional.energy.description': 'Ruwe kracht. Natuurlijk momentum.',
      'functional.calm.name': 'HERSTEL',
      'functional.calm.subtitle': 'Ontspannen',
      'functional.calm.spice': 'Reishi x Zout',
      'functional.calm.benefits': 'Clean label. 2 ingrediënten.',
      'functional.calm.description': 'Zachte angstverlichting. Soepele overgangen.',
      'functional.rest.name': 'HERSTEL',
      'functional.rest.subtitle': 'Goed Slapen',
      'functional.rest.spice': 'Gerookt Zout',
      'functional.rest.benefits': 'Reishi, Valeriaan, Magnesium',
      'functional.rest.description': 'Diepe rust. Kwaliteitsslaap.',
      'functional.waitlist.title': 'Inschrijven op de Wachtlijst',
      'functional.waitlist.description': 'Wees de eerste die onze adaptogene specerijen reeks ontvangt. Schrijf je in op de wachtlijst voor vroege toegang wanneer we lanceren in UK en EU markten.',
      'functional.waitlist.note': 'Beperkte vroege toegang. Geen betaling vereist',
      'functional.waitlist.placeholder': 'jouw@email.com',
      'functional.waitlist.cta': 'Inschrijven',
      'functional.waitlist.success': '✓ Je staat op de lijst',
      'howItWorks.title': 'Hoe Het Werkt',
      'howItWorks.subtitle': 'Simpel. Betekenisvol. Impactvol.',
      'howItWorks.step1.title': 'Inschrijven op de Wachtlijst',
      'howItWorks.step1.description': 'Schrijf je in op de wachtlijst voor vroege toegang tot onze adaptogene specerijen reeks. Geen betaling vereist.',
      'howItWorks.step2.title': 'Kruid Je Maaltijden',
      'howItWorks.step2.description': 'Voeg functionele smaak toe aan je dagelijkse koken. Ondersteun je mentale welzijn bij elke maaltijd.',
      'howItWorks.step3.title': 'Financier Toegang tot Therapie',
      'howItWorks.step3.description': 'Elke 100 verkochte verpakkingen financieren één uur therapie voor iemand die het nodig heeft. Smaak die teruggeeft.',
      'howItWorks.closing.title': 'Betere mentale gezondheid begint in je keuken.',
      'howItWorks.closing.subtitle': 'Elke maaltijd is een kans om voor jezelf en anderen te zorgen.',
      'comingSoon.coach.badge': 'Binnenkort',
      'comingSoon.coach.title': 'Jouw AI Coach',
      'comingSoon.coach.description': 'Zachte dagelijkse ondersteuning tussen therapiesessies. Volg je stemming, ontvang gepersonaliseerde voedingsadviezen en bereid inzichten voor je therapeut voor.',
      'comingSoon.coach.form.title': 'Doe Mee aan Beta',
      'comingSoon.coach.form.placeholder': 'jouw@email.com',
      'comingSoon.coach.form.cta': 'Doe Mee aan Wachtlijst',
      'comingSoon.coach.form.success': '✓ Je staat op de lijst',
      'comingSoon.coach.feature1.title': 'Dagelijkse Check-ins',
      'comingSoon.coach.feature1.description': 'Snelle stemmingsvolging die je patronen en ritmes leert.',
      'comingSoon.coach.feature2.title': 'Micro-Acties',
      'comingSoon.coach.feature2.description': 'Kleine, haalbare voedingssuggesties afgestemd op hoe je je voelt.',
      'comingSoon.coach.feature3.title': 'Therapie Voorbereiding',
      'comingSoon.coach.feature3.description': 'Gestructureerde inzichten om mee te nemen naar je volgende sessie.',
      'comingSoon.fund.badge': 'Binnenkort',
      'comingSoon.fund.title': 'Het Fonds',
      'comingSoon.fund.description': 'Toegang tot geestelijke gezondheidszorg uitbreiden. Vraag gefinancierde therapiesessies aan of draag bij om anderen te helpen toegang te krijgen tot de ondersteuning die ze nodig hebben.',
      'comingSoon.fund.form.title': 'Doe Mee aan Wachtlijst',
      'comingSoon.fund.form.placeholder': 'jouw@email.com',
      'comingSoon.fund.form.cta': 'Krijg Melding',
      'comingSoon.fund.form.success': '✓ Je staat op de lijst',
      'comingSoon.fund.feature1.title': 'Vraag Ondersteuning Aan',
      'comingSoon.fund.feature1.description': 'Krijg toegang tot gefinancierde therapiesessies wanneer je ze het meest nodig hebt.',
      'comingSoon.fund.feature2.title': 'Gemeenschap-Gedreven',
      'comingSoon.fund.feature2.description': 'Elke aankoop draagt bij. Elke bijdrage telt.',
      'comingSoon.fund.feature3.title': 'Transparante Impact',
      'comingSoon.fund.feature3.description': 'Zie precies hoe jouw steun echte mensen helpt toegang te krijgen tot zorg.',
      'roadmap.badge': 'Wat Komt Er',
      'roadmap.title': 'Voorbij de Specerijen',
      'roadmap.subtitle': 'Zodra we lanceren voor onze eerste wachtlijstleden, openen we twee aanvullende programmas.',
      'roadmap.coach.title': 'AI Welzijnscoach',
      'roadmap.coach.description': 'Zachte dagelijkse ondersteuning tussen therapiesessies. Stemmingsvolging, voedingsadviezen en therapie-voorbereiding.',
      'roadmap.coach.status': 'Opent Na Lancering',
      'roadmap.fund.title': 'Het Therapiefonds',
      'roadmap.fund.description': 'Elke 100 verpakkingen financieren één uur therapie. Vraag gefinancierde sessies aan of draag bij om toegang uit te breiden.',
      'roadmap.fund.status': 'Opent Na Lancering',
      'roadmap.note': 'Schrijf je vandaag in op de wachtlijst om beide te ontgrendelen.',
      'social.title': 'Volg de Neurolution',
      'social.handle': '@thisisneurospice',
      'footer.title': 'Kruid met Reden',
      'footer.subtitle': 'Krijg smaak. Geef therapie.',
      'footer.cta.waitlist': 'Wachtlijst',
      'footer.cta.coach': 'Doe Mee aan Beta',
      'footer.cta.fund': 'Doe Mee aan Wachtlijst',
      'floating.text': 'Doe mee aan de Neurolution'
    }
  };
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-456" data-mp="1o46637e6h7pn">
      {children}
    </LanguageContext.Provider>;
};